/**
 * types/index.ts
 * ------------------------------------------------------------------
 * Domain model for the LKC Academic Platform.
 *
 * These types are the contract every feature module builds against.
 * Key LKC-specific design decisions are documented inline:
 *   - 6-role model incl. `editor` and `hoy` (Head of Year)
 *   - Term-aware marks: monthly CA + separate exam mark in T2/T3
 *   - Parent contact (email + WhatsApp) on students
 *   - Generated/stored reports for class-teacher viewing + bulk send
 * ------------------------------------------------------------------
 */

/* ==================================================================
   ROLES & USERS
   ================================================================== */

/**
 * LKC role model.
 *
 * - admin         Full control. ONLY role that can generate final reports.
 * - headteacher   Setup + oversight, no user management.
 * - editor        NEW. Reviews generated reports, flags grammar/errors
 *                 (informational only), and may directly edit HOY +
 *                 class-teacher comments on the generated report.
 * - hoy           NEW. Head of Year — enters the HOD/principal comment
 *                 on behalf of the principal, per student.
 * - classTeacher  Enters ONE combined attendance+conduct comment.
 *                 Can view own class reports ONLY after generation.
 * - subjectTeacher Enters marks for assigned subjects only.
 *                 CANNOT view final reports at all.
 * - admissions    NEW. Office staff who manage student intake only.
 *                 Can access the Admissions module and nothing else.
 */
export type Role =
  | 'admin'
  | 'systemAdmin'
  | 'academicAdmin'
  | 'superAcademicAdmin'
  | 'operationsAdmin'
  | 'headteacher'
  | 'editor'
  | 'hoy'
  | 'classTeacher'
  | 'subjectTeacher'
  | 'admissions'
  | 'parent'
  | 'hod'
  | 'hr'
  | 'hrAdmin'
  | 'hrOfficer'
  | 'payrollOfficer'
  | 'bursar'
  | 'financeAdmin'
  | 'feesOfficer'
  | 'examsOfficer'
  | 'staff'
  | 'boardingMaster'
  | 'bursar_assistant'
  /** Media / communications: downloads pupils' photographs class by class
   *  (yearbook, ID cards, website). Sees pupils' names and classes and their
   *  photos — nothing else. Oct 2026. */
  | 'media'
  /** A pupil's own login to the study portal (ported from Al-Nur, Oct 2026).
   *  Its one grant is canAccessStudentPortal. Not issuable yet: the server's
   *  ASSIGNABLE_ROLES leaves it out until the rules sweep lands. */
  | 'student';

export interface AppUser {
  id: string; // == uid
  uid: string;
  email: string;
  displayName: string;
  /** ISO timestamp set the first time a parent dismisses the portal
   *  welcome modal. Absent = they haven't seen it yet. */
  parentWelcomeSeenAt?: string;
  role: Role;
  /** Multi-role support: the full set of roles this user holds. When
   *  present, UI permissions are the union of all of these (the single
   *  `role` above stays the primary, used for hierarchy + claims). */
  roles?: Role[];
  /** Link to a teacher record (for class/subject teachers, HOY, editors). */
  linkedTeacherId?: string;
  /**
   * Link to an HR staff record (hrStaff doc id). Drives the staff
   * self-service view ("My Pay & HR") and scopes the Firestore reads so
   * the user sees only their own payslips / leave / tax certificate.
   */
  linkedStaffId?: string;
  /**
   * For HOY: the form levels / year groups this HOY is responsible for.
   * e.g. ['1','2'] or ['AS','A2']. Used to scope which students' HOD
   * comments they may enter.
   */
  hoyForms?: string[];
  /**
   * Boarding houses this user is housemaster of, e.g. ['boys'].
   * Mirrors hoyForms/hodDepartments: the ROLE says what they may do, this
   * says who to. A boardingMaster with none listed supervises nobody — the
   * safe default, since the alternative is a new account seeing the whole
   * school's reports the moment the role is granted.
   */
  boardingHouses?: string[];
  /**
   * For editors: the form levels this editor is responsible for
   * reviewing. Flags raised on a student in one of these forms are
   * routed to this editor as a notification. Mirrors hoyForms.
   * e.g. ['1','2'] or ['AS','A2'].
   */
  editorForms?: string[];
  /**
   * For HOD: the departments this HOD oversees.
   * e.g. ['Mathematics', 'Sciences']. Used to scope which
   * subjects/teachers they see in analytics.
   */
  hodDepartments?: string[];
  isActive: boolean;
  /** For parent role: links this user to guardian records + children. */
  parentAccount?: ParentAccountLink;
  /** For student role: links this login to the student's own record.
   *  Ported from Al-Nur with the student portal. Must be on the users
   *  self-update block-list before any pupil login exists (it is what the
   *  rules' myStudentId() reads). */
  studentAccount?: StudentAccountLink;
  /** A pupil's own syllabus options, syllabus code -> option topic ('0445' ->
   *  '2' for Materials). Self-edited; it narrows what the portal shows and
   *  gates nothing (lib/study/pupilOption). */
  studyOptions?: Record<string, string>;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  lastLoginAt?: string;
  /** Web push is ON by default: absent means opted in. Only an explicit
   *  `true` silences it, and it is honoured server-side in
   *  functions/lib/push.js for every push in the system. */
  pushOptOut?: boolean;
  passwordChangedAt?: string;
}

/* ==================================================================
   SETUP ENTITIES
   ================================================================== */

export interface Teacher {
  id: string;
  title: string;
  initials: string;
  lastName: string;
  fullName: string;
  displayName: string;
  email?: string;
  /** Department this teacher belongs to (e.g. 'Mathematics', 'Sciences') */
  department?: string;
  /** Phone / mobile number */
  phone?: string;
  /** Qualifications (e.g. 'B.Ed, M.Sc Mathematics') */
  qualifications?: string;
  /** Date teacher joined the school (ISO) */
  dateJoined?: string;
  /** Employee / staff ID */
  staffId?: string;
  /** Employment status */
  employmentStatus?: 'full-time' | 'part-time' | 'contract' | 'intern';
  /** Any additional notes */
  notes?: string;
  /** form level -> subjectIds taught, e.g. { "3": ["maths","english"] } */
  teachingAssignments?: Record<string, string[]>;
  /**
   * Precise class-level teaching: subjectId -> classIds taught.
   * This is the authoritative link used to resolve a student's
   * subject teacher. e.g. { "maths": ["cls_5A","cls_5B"] }.
   * teachingAssignments (form-level) is kept as a fallback.
   */
  classAssignments?: Record<string, string[]>;
  isClassTeacher?: boolean;
  classTeacherOf?: string; // classId
  isAssistantClassTeacher?: boolean;
  assistantClassTeacherOf?: string[]; // classIds
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * Student record.
 * NEW for LKC: parentContact (email + WhatsApp) powers report delivery.
 */
export interface Student {
  id: string;
  fullName: string;
  /**
   * The pupil's photograph. Written ONLY by the server function that cleans
   * each upload (functions/studentPhotos.js); the rules refuse it from any
   * browser. Holds storage PATHS, never a shareable download link — a
   * child's photo is fetched through the storage rules every time.
   * Top-level on purpose: the admissions form rewrites `admissions` whole
   * on every save, and would wipe a photo kept inside it.
   */
  photo?: import('@/lib/studentPhotoPolicy').StudentPhoto;
  currentClass: string; // classId
  /** 'M' | 'F'. '' is a SENTINEL for a not-yet-enrolled (status:'pending')
   *  student whose real gender isn't known yet — real gender is required
   *  before enrolment flips status to 'active'. Never '' for active pupils. */
  gender: 'M' | 'F' | '';
  /** Canonical date of birth (ISO). Single source of truth; the
   *  admissions intake feeds this and no longer stores its own copy. */
  dateOfBirth?: string;
  admissionNumber?: string;
  /** subjectIds the student is enrolled in */
  subjects: string[];
  /** subjectId -> teacherId */
  subjectTeachers: Record<string, string>;
  /**
   * Enrolment state. Absent/'active' = a current student. 'left' = has
   * graduated / withdrawn / transferred — kept for records but EXCLUDED
   * from active rosters (DataContext.activeStudents). Set by the
   * Promotion Wizard at year-end or manually from the Student profile.
   *
   * 'pending' = exists in Pastel (finance) but not yet enrolled — held in
   * the Admissions "Pending" tab with a seeded opening balance. EXCLUDED
   * from activeStudents AND from every raw-`students` roster/billing
   * consumer (see the status!=='pending' guards). Real gender + class are
   * required before enrolment flips it to 'active'.
   */
  status?: 'active' | 'left' | 'pending';
  /** ISO date the student was marked 'left'. */
  exitDate?: string;
  /** Why they left (for records) — mirrors the promotion action. */
  exitReason?: 'graduated' | 'withdrawn' | 'transferred';
  /** Class they were in when they left (currentClass is preserved as-is). */
  lastClassId?: string;
  /** NEW — parent/guardian contact for report delivery (#5) */
  parentContact?: ParentContact;
  /** NEW — full admissions intake data (Feature E). */
  admissions?: AdmissionsData;
  /**
   * NEW — links this student to a Family (`reportFamilies`). Siblings
   * share the same `familyId`. Optional + additive: existing students
   * have none until grouped, and no academic feature requires it. It is
   * the authoritative grouping key for comms de-duplication and the
   * finance module (combined family billing, sibling discounts).
   */
  familyId?: string;
  /** Pastel customer code (e.g. 'BAL851'), recorded by the balance
   *  reconciliation so future syncs are a keyed join. Written to live data
   *  by the reconciliation script; declared here so the UI can read it. */
  pastelCode?: string;
  /** Optional per-student billing override — takes precedence over the
   *  family plan and the run default. Lets a single student be put on a
   *  different instalment arrangement. */
  billingPlan?: FamilyBillingPlan;
  /** One-shot marker set at admission (Pula amount of the development fee for
   *  the level admitted into). The next billing run adds it as a line item and
   *  clears it, so it's charged exactly once. Not set on internal promotion. */
  pendingDevelopmentFee?: number;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ParentContact {
  guardianName?: string;
  email?: string;
  /** E.164-ish WhatsApp number, e.g. +2677XXXXXXX */
  whatsapp?: string;
  /** If true, parent has consented to electronic report delivery */
  consentToSend?: boolean;
}

/* ==================================================================
   ADMISSIONS DATA MODEL (Feature E)
   ==================================================================

   The Admissions module is the canonical source of student identity
   data. All downstream features (Student Profile v2, parent accounts,
   attendance, discipline, promotion) read from this structure.

   Key design choices:
     - Guardians are an array with role labels (not hardcoded
       father/mother fields) to handle real family situations.
     - Each guardian has a stable `id` for future parent-account
       linking (Feature B).
     - Exactly one guardian must be `isPrimaryContact` — this is
       who BulkSend delivers reports to.
     - Medical info is flat free-text, not over-structured.
     - The `admissions` field on Student is optional — existing
       students imported via BulkImport won't have it until
       backfilled through the Admissions form.
   ================================================================== */

export type GuardianRole =
  | 'father'
  | 'mother'
  | 'guardian'
  | 'stepfather'
  | 'stepmother'
  | 'grandparent'
  | 'other';

export interface Guardian {
  /** Stable id (nanoid) — used for future parent-account linking. */
  id: string;
  role: GuardianRole;
  /** Free-text label when role='other', e.g. "Uncle", "Aunt". */
  roleLabel?: string;
  fullName: string;
  idNumber?: string;
  /** Guardian's own nationality (distinct from the student's). */
  nationality?: string;
  occupation?: string;
  employer?: string;
  phone?: string;
  /** Employer / work landline. */
  workPhone?: string;
  /** Home landline. */
  homeTel?: string;
  /** E.164-ish WhatsApp number, e.g. +2677XXXXXXX */
  whatsapp?: string;
  email?: string;
  address?: string;
  /** This guardian is (one of) the fee-responsible party. */
  responsibleForFees?: boolean;
  /** Guardian-specific emergency contact (paper form §3). */
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  /** Exactly one guardian per student must be primary. */
  isPrimaryContact: boolean;
  /** Consent for electronic report delivery. */
  consentToSend?: boolean;
  /** Consent for WhatsApp messages. */
  consentToWhatsapp?: boolean;
}

export interface MedicalInfo {
  doctorName?: string;
  doctorPhone?: string;
  medicalAidProvider?: string;
  medicalAidNumber?: string;
  allergies?: string;
  chronicConditions?: string;
  medications?: string;
  bloodType?: string;
  /** Explicit Y/N that the child has a medical condition (paper §5). */
  hasMedicalCondition?: boolean;
  /** Free-text explanation when hasMedicalCondition is true. */
  medicalConditionDetails?: string;
  /** Consent to move/treat the child in an emergency (paper §5). */
  emergencyTreatmentConsent?: boolean;
  /** If consent withheld, what action the school should take instead. */
  emergencyTreatmentNoConsentAction?: string;
}

/** One previous-school row (paper form §4). */
export interface PreviousSchoolEntry {
  name?: string;
  year?: string;
  formClass?: string;
  contact?: string;
}

/** Structured previous-schooling history (paper form §4). Replaces the
 *  legacy single `previousSchool` string — that string is still written
 *  (mirrors `last.name`) for backward-compat with existing records and
 *  the admit path. */
export interface PreviousSchoolsInfo {
  last?: PreviousSchoolEntry;
  /** Up to 3 earlier schools. */
  others?: PreviousSchoolEntry[];
}

/** "Another child already at the school" section (paper form §6). */
export interface SiblingInfo {
  hasSiblingAtSchool?: boolean;
  sibling1?: { names?: string; surname?: string };
  sibling2?: { names?: string; surname?: string };
  /** Parent/guardian of the sibling(s) — for linking families. */
  siblingParentNames?: string;
  siblingParentEmail?: string;
}

/**
 * Full admissions intake data, stored as `student.admissions`.
 *
 * Personal fields here complement (not replace) the top-level
 * Student fields (fullName, gender, admissionNumber) which are
 * kept for backward compatibility with existing features.
 */
/**
 * Entrance / placement test captured during admission. Admissions staff
 * record which subjects a pupil sat and the mark obtained; different
 * pupils may sit different subjects. Subjects are chosen from the
 * school's subject list. Results are shown read-only in the student's
 * History tab — they are an admission record, not live enrolment.
 */
export interface EntranceTestResult {
  /** subjectId from the school's subjects list (reportSubjects). */
  subjectId: string;
  /** Raw mark obtained. */
  mark: number;
  /** Optional maximum the mark was out of (e.g. 100 for a percentage). */
  outOf?: number;
}
export interface EntranceTest {
  /** Whether the pupil sat an entrance test at all. */
  taken: boolean;
  /** ISO date the test was sat (optional). */
  testDate?: string;
  /** One row per subject sat. */
  results: EntranceTestResult[];
  /** Free-text remarks from the admissions process (optional). */
  notes?: string;
}

export interface AdmissionsData {
  /** Separate first/surname for formal addressing & sorting. */
  firstName?: string;
  surname?: string;
  dateOfBirth?: string;
  /** Student's place of birth (paper §1; carried from the application). */
  placeOfBirth?: string;
  nationality?: string;
  /** Student national ID number (migrated from legacy where present). */
  nationalId?: string;
  /** Student passport number. */
  passportNumber?: string;
  /** Combined ID/passport number as captured on the application form
   *  (the public form collects a single field, not separate ID vs passport). */
  idPassportNumber?: string;
  /** Student's own email address (students don't log in; reference only). */
  studentEmail?: string;
  /** Student's own phone (reference only; carried from the application). */
  studentPhone?: string;
  homeLanguage?: string;
  religion?: string;
  address?: string;
  /** Guardians array — canonical parent/guardian data. */
  guardians: Guardian[];
  /** Medical profile. */
  medical?: MedicalInfo;
  /** Emergency contact (if different from guardians). */
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactEmail?: string;
  /** Previous school name (legacy single-string; kept for backward-compat). */
  previousSchool?: string;
  /** Structured previous-schooling history (paper §4). */
  previousSchools?: PreviousSchoolsInfo;
  /** Enrollment date (ISO). */
  dateOfAdmission?: string;
  /** Day scholar or boarder. */
  boardingStatus?: 'day' | 'boarding';
  /** Which boarding house, when boarding. Matched against
   *  AppUser.boardingHouses to decide what a housemaster may see. */
  boardingHouse?: string;
  /** @deprecated Shareable download links are no longer stored for
   *  children's photos — see Student.photo. Kept only so an older record
   *  still type-checks; nothing writes these any more, and none were ever
   *  written in production. */
  photoUrl?: string;
  /** @deprecated See photoUrl. */
  thumbnailUrl?: string;
  /** @deprecated See photoUrl. */
  photoUploadedAt?: string;
  /** Any additional notes from the admissions process. */
  notes?: string;
  /** Entrance/placement test results captured at admission. Optional —
   *  set only when the pupil sat a test. Displayed in the History tab. */
  entranceTest?: EntranceTest;
  updatedAt?: string;
  updatedBy?: string;
}

export interface Subject {
  id: string;
  name: string;
  shortName?: string;
  /**
   * Subject FAMILY taxonomy ('sciences', 'core', 'commercial', …) used
   * for display grouping only. This is NOT the academic department —
   * for that use Subject.department / lib/departments.departmentOfSubject.
   */
  category?: string;
  /**
   * Canonical academic department this subject belongs to, in the same
   * vocabulary as Teacher.department / AppUser.hodDepartments
   * (e.g. 'Mathematics', 'Sciences'). Optional authoritative override:
   * when unset, the department is derived from the teachers who teach it
   * (see lib/departments.departmentOfSubject).
   */
  department?: string;
  /**
   * Cambridge IGCSE grading tier for this subject. Absent = 'extended'
   * (the historic A*..E,U scale). 'core' grades C..G,U and CAPS AT C.
   * Resolved to a scheme via lib/grading.schemeForSubject and threaded
   * into every grade-derivation call site.
   */
  gradeScheme?: 'extended' | 'core';
  hasExam?: boolean;
  passMark?: number;
  displayOrder?: number;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
}

/* ------------------------------------------------------------------
 * Exam sessions (Exams · seating v2). A persisted exam sitting: its
 * candidate sources (form+subject), the rooms, and the explicit seat
 * assignment (so manual edits + locked seats survive reloads).
 * ---------------------------------------------------------------- */
export interface ExamSessionSource {
  form: string;
  subjectId: string;
}
export interface ExamSessionRoom {
  id: string;
  name: string;
  capacity: number;
  cols: number;
}
export interface ExamSeatAssignment {
  studentId: string;
  roomId: string;
  seat: number; // 1-based position within the room
  locked?: boolean; // manual placement protected from re-auto-assign
}
export interface ExamSession {
  id: string;
  name: string;
  year: string;
  term: string;
  /** ISO date (yyyy-mm-dd) + times for the sitting / timetable. */
  date?: string;
  startTime?: string;
  endTime?: string;
  sources: ExamSessionSource[];
  mix: boolean;
  rooms: ExamSessionRoom[];
  seats: ExamSeatAssignment[];
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface SchoolClass {
  id: string;
  name: string; // e.g. "Form 3 A"
  /** Form level: '1'..'5' | 'AS' | 'A2' */
  form: string;
  stream?: string;
  /** Academic division (from Al-Nur's timetable, 31 Oct 2026). LKC has one
   *  secondary division, so this is unset and every class reads as secondary. */
  division?: import('@/lib/divisions').Division;
  /** Academic calendar/cohort. The January intake (default/undefined) runs the
   *  school-wide term cycle; the 'june' intake (AS/6L + A2 June) is out of sync
   *  — it is in ITS Term 1 while the school is in Term 2, so its reports are
   *  CA-only, comment-only, "Term 1"-labelled and carry no exam. See
   *  src/lib/cohort.ts (isJuneCohort). */
  calendar?: 'january' | 'june';
  classTeacherId?: string;
  assistantClassTeacherId?: string;
  /**
   * Archived / non-academic class (e.g. "Alumni" holding students who
   * have left or completed). Its students are EXCLUDED from active
   * rosters — compliance, attendance, analytics, etc. — via
   * DataContext.activeStudents. Kept for record-keeping, not teaching.
   * (Interim mechanism until a per-student status lands in Phase 2.)
   */
  archived?: boolean;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
}

/* ==================================================================
   MARKS — TERM-AWARE MODEL (feature #8)
   ==================================================================

   Term 1:
     - Teachers enter monthly continuous-assessment (CA) marks.
     - termMark = average of CA months.
     - reportGrade derived from termMark.
     - No exam field shown.

   Terms 2 & 3:
     - Monthly CA marks still entered -> caMark (the "term/CA mark").
     - A SEPARATE exam mark is entered.
     - Report shows BOTH caMark and exam side by side per subject.
     - reportGrade derived from the EXAM mark.

   A mark value may be a number (0–100) or the string 'ABS' (absent),
   mirroring v1 behaviour. `null`/'' means "not entered yet".
   ================================================================== */

export type MarkValue = number | 'ABS';
export type MaybeMark = MarkValue | '' | null;

/** Calendar months a term can span (config-driven, see grading.ts). */
export type CAMonths = Record<string, MaybeMark>;

export interface SubjectMarkEntry {
  /** Monthly continuous-assessment marks: { "Jan": 72, "Feb": "ABS" } */
  ca: CAMonths;
  /** Computed average of entered CA months (rounded). */
  caMark: number | null;
  /** Exam mark — used in Term 2 & Term 3 only. */
  exam?: MaybeMark;
  /**
   * The mark that appears as the subject's primary "term mark" on the
   * report. T1: == caMark. T2/T3: == caMark (exam shown separately).
   */
  termMark: number | null;
  /**
   * Grade shown on the report.
   * T1: grade(termMark). T2/T3: grade(exam).
   */
  reportGrade: string;
  /** Subject teacher's comment for this student/subject. */
  comment: string;
  /**
   * Teacher credited for this mark, STAMPED at save time from the
   * student's then-current subjectTeachers[subjectId]. Absent on older
   * entries — per-term attribution consumers (report cards, teacher/dept
   * analysis) read `teacherId ?? student.subjectTeachers[subjectId]`, so
   * a mid-year Teacher Swap never rewrites who is credited for prior
   * terms. See seed/backfill and lib/teacherLink.
   */
  teacherId?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * Firestore doc id: `${year}-${term}-${classId}-${subjectId}`
 * (kept compatible with v1's `marks` collection key shape).
 */
export interface MarksDocument {
  year: string;
  term: string;
  classId: string;
  subjectId: string;
  /** studentId -> entry */
  entries: Record<string, SubjectMarkEntry>;
  updatedAt?: string;
}

/* ==================================================================
   ATTENDANCE
   ================================================================== */

export interface AttendanceDocument {
  year: string;
  term: string;
  classId: string;
  /** studentId -> totals */
  studentTotals: Record<
    string,
    { daysPresent: number; daysAbsent: number; possibleDays?: number }
  >;
  /** Optional per-month breakdown (kept for parity with v1). */
  monthly?: Record<string, Record<string, number>>;
  updatedAt?: string;
}

/* ==================================================================
   COMMENTS (feature #2)
   ==================================================================

   LKC simplifies v1's attire/conduct/progress trio:

   - classTeacherComment: ONE comment covering attendance & conduct,
     written by the class teacher.
   - hodComment: the "HOD comment on behalf of the principal",
     written by the Head of Year (HOY).

   Both are editable directly by `editor` users on the generated
   report (feature #3) — edits are tracked with editedBy/editedAt.
   ================================================================== */

export interface StudentReportComments {
  /** Class teacher's single attendance + conduct comment. */
  classTeacherComment: string;
  /** HOY's HOD/principal comment. */
  hodComment: string;
  /** Audit: who last touched each, and whether an editor overrode it. */
  classTeacherCommentMeta?: CommentMeta;
  hodCommentMeta?: CommentMeta;
}

export interface CommentMeta {
  authorId?: string;
  authorRole?: Role;
  updatedAt?: string;
  /** Set when an editor overrides the original on the report. */
  editedByEditor?: boolean;
  editorId?: string;
  editorName?: string;
  editedAt?: string;
}

/**
 * Firestore doc id: `${year}-${term}-${classId}`
 * collection: `reportComments`
 */
export interface ReportCommentsDocument {
  id?: string;
  year: string;
  term: string;
  classId: string;
  /** studentId -> comments */
  entries: Record<string, StudentReportComments>;
  updatedAt?: string;
}

/* ==================================================================
   COMMENT FLAGGING (feature #1)
   ==================================================================

   In LKC, ONLY `editor` users may raise flags. A flag is purely
   INFORMATIONAL — it notifies the concerned teacher of an error.
   Correction is done by the editor (directly on the report) or by
   the teacher on the original marksheet. There is no
   "submit revision -> reviewer resolves" loop from v1; an editor
   simply opens, acknowledges, or closes a flag.
   ================================================================== */

export type FlagStatus = 'open' | 'acknowledged' | 'resolved';

export type FlagTarget =
  | 'subjectComment'
  | 'classTeacherComment'
  | 'hodComment'
  | 'mark';

export interface CommentFlag {
  id: string;
  status: FlagStatus;
  target: FlagTarget;

  year: string;
  term: string;
  classId: string;
  className?: string;
  studentId: string;
  studentName: string;

  /** Present when target === 'subjectComment' | 'mark'. */
  subjectId?: string;
  subjectName?: string;

  /** The teacher who should be informed of the issue. */
  teacherId?: string;
  teacherName?: string;

  /** Snapshot of the flagged text/mark at flag time. */
  originalText?: string;

  reason: string;
  reasonLabel?: string;
  notes?: string;

  /** Editor who raised it. */
  flaggedById: string;
  flaggedByName: string;
  createdAt: string;

  acknowledgedAt?: string;
  resolvedAt?: string;
  resolvedById?: string;
}

/* ==================================================================
   GENERATED REPORTS (features #4 & #6)
   ==================================================================

   Final reports are generated by ADMIN only, stored in Firebase
   Storage, and indexed in Firestore so:
     - class teachers can VIEW their own class reports after generation
     - admins can BULK email/WhatsApp them per class
   ================================================================== */

export type ReportSendChannel = 'email' | 'whatsapp';
export type ReportSendStatus =
  | 'not_sent'
  | 'queued'
  | 'sent'
  | 'failed'
  | 'skipped';

export interface GeneratedReport {
  id: string; // `${year}-${term}-${classId}-${studentId}`
  year: string;
  term: string;
  classId: string;
  className: string;
  studentId: string;
  studentName: string;

  /** Firebase Storage path + download URL of the rendered PDF. */
  storagePath: string;
  downloadUrl?: string;

  generatedAt: string;
  generatedById: string;
  generatedByName: string;

  /** Delivery tracking for bulk send (#6). */
  send: {
    email: ReportSendStatus;
    whatsapp: ReportSendStatus;
    lastAttemptAt?: string;
    lastError?: string;
  };
}

/**
 * A generation "batch" — one run of admin generating a class's reports.
 * collection: `reportBatches`, id `${year}-${term}-${classId}`
 */
export interface ReportBatch {
  id: string;
  year: string;
  term: string;
  classId: string;
  className: string;
  studentIds: string[];
  generatedAt: string;
  generatedById: string;
  generatedByName: string;
  /** Lets class teachers know their class is ready to view. */
  status: 'generating' | 'ready' | 'partial' | 'failed';
  /**
   * P5 audit: set when an admin generated despite unresolved
   * flags. Records that the gate was deliberately overridden and
   * how many flags were open at the time.
   */
  flagOverride?: boolean;
  flagOverrideCount?: number;
}

/* ==================================================================
   TERM STATUS / LOCKING (parity with v1 admin controls)
   ================================================================== */

export type TermState = 'open' | 'closed' | 'locked';

/**
 * Round 4: deadlines admin sets for mark entry. Read daily by the
 * scheduled Cloud Function `processDeadlineReminders` which:
 *   - posts warning announcements at 7d, 3d, 1d, and on-the-day
 *   - posts a "deadline passed" announcement the day after
 *   - auto-locks the relevant CA month the day after its deadline
 *   - sends targeted notifications to teachers with missing marks
 *
 * Field shape:
 *   ca: { [caLabel]: 'YYYY-MM-DD' }
 *      e.g. { 'CA1': '2026-04-15', 'CA2': '2026-05-30' }
 *   termDeadline: 'YYYY-MM-DD'  - final deadline for all marks + exam
 *
 * A missing/empty deadline disables reminders for that assessment.
 * Admin can set them gradually.
 */
export interface MarkEntryDeadlines {
  ca?: Record<string, string>;
  termDeadline?: string;
  /**
   * Round 11: per-CA "notify parents" date (YYYY-MM-DD). When set,
   * the scheduled function `processParentCaNotifications` sends a
   * single bulk in-app + email to every active parent on this date,
   * once. Default suggested by the UI is deadline + 1 day, but admin
   * can pick any date or leave blank to skip.
   */
  caParentNotify?: Record<string, string>;
}

export interface TermStatusDocument {
  id: string; // `${year}_${term}`
  year: string;
  term: string;
  status: TermState;
  /**
   * P6: per-assessment-month locks within an open term, keyed by
   * CA month name (e.g. { January: true }). A locked month blocks
   * mark edits for that month even while the term is 'open'. Term
   * status 'closed'/'locked' still overrides everything.
   */
  monthLocks?: Record<string, boolean>;
  /**
   * Round 4: admin-set deadlines for mark entry. See above.
   */
  deadlines?: MarkEntryDeadlines;
  /**
   * Round 4: tracks which reminder announcements have already
   * been generated this term so the scheduled function doesn't
   * duplicate them across days. Keyed:
   *   `ca:${caLabel}:${stage}`      stage in '7d' | '3d' | '1d' | 'day' | 'past'
   *   `term:${stage}`               same stages for term deadline
   * Value is the announcement doc id that was created.
   */
  generatedReminders?: Record<string, string>;
  /**
   * Round 11: sentinel recording which CA parent-notifications
   * have already been sent. Keyed `ca:${caLabel}`, value is the
   * ISO timestamp the bulk send completed. Used by both the
   * scheduled function and the "Send now" callable to keep
   * notifications idempotent.
   */
  parentNotifications?: Record<string, string>;
  /**
   * Round 12: per-CA publish state. When a CA label appears in
   * this map, parents see that CA's marks in their portal. Absent
   * = hidden. Removing the entry (un-publish) hides again.
   *
   * Setting an entry here is the SINGLE trigger for both:
   *   - Parent portal visibility (filtered client-side)
   *   - Parent notification (server-side, on publish)
   */
  publishedCas?: Record<string, { at: string; by: string }>;
  /**
   * Round 12: term-final publish state. When set, the term-final
   * exam column appears in the parent portal's Academic tab.
   * Independent of publishedCas so the school can publish CAs
   * progressively but hold the final until the exam is fully
   * reviewed.
   */
  publishedTermFinal?: { at: string; by: string };
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * P-CONFIG: per-term assessment configuration.
 *
 * Configured by admins via the Assessment Config panel; defines how
 * many CAs (continuous assessments) a term has and what each one is
 * called on Mark Entry. The labels become the keys under `ca` in
 * SubjectMarkEntry — `deriveMark` doesn't care what they're named,
 * it averages whatever values are present.
 *
 * If no document exists for a term, the engine falls back to the
 * hardcoded TERM_CA_MONTHS in grading.ts so a fresh deployment
 * still works on day zero. That fallback is intentional — never
 * silently produce zero CAs.
 */
export interface AssessmentConfigDocument {
  id: string; // term key, e.g. 'Term1'
  term: string;
  /** Ordered list of CA labels. The length is the CA count. */
  caLabels: string[];
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * P-CONFIG: per-(formLevel, term) exam title configuration.
 *
 * Same admin panel as AssessmentConfig; same access gate
 * (canConfigureAssessments). Lets LKC label the exam column on
 * the report card differently for different forms/terms — e.g.
 * Form 5 Term 2 = "Mock Exam", Forms 1–4 Term 2 = "Mid-year Exam".
 *
 * The grading math does NOT change. This is purely the column
 * HEADER text on the printed report.
 *
 * Doc ID: `${formLevel}_${term}` (e.g. "5_Term2"). Falls back to
 * the generic "Exam" if no document exists for the form+term
 * pair, so no blank header is ever rendered.
 */
export interface ExamTitleDocument {
  id: string; // `${formLevel}_${term}`
  formLevel: string; // e.g. '1', '2', '3', '4', '5', 'AS', 'A2'
  term: string; // 'Term1' | 'Term2' | 'Term3'
  title: string; // e.g. 'Mock Exam', 'Mid-year Exam'
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * P-CONFIG: per-(year, term) report notices.
 *
 * Admin-editable text blocks rendered on the printed report card —
 * one on the cover (front page), one on the back cover. Independent
 * fields; either can be empty. If both are empty for a given (year,
 * term), no notice block is rendered (silent absence — the cleaner
 * default we agreed on).
 *
 * Same admin-only gate as the other config (canConfigureAssessments).
 * Doc ID: `${year}_${term}` (matches reportTermStatus convention).
 */
export interface ReportNoticeDocument {
  id: string; // `${year}_${term}`
  year: string;
  term: string;
  frontPage: string; // shown on cover; empty = no front-page notice
  backPage: string; // shown on back cover under "NOTICES" header
  updatedAt?: string;
  updatedBy?: string;
}

/* ==================================================================
   DAILY ATTENDANCE (Step 4)
   ================================================================== */

export type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused' | 'sick';

export interface DailyAttendanceDoc {
  id?: string;
  classId: string;
  className: string;
  date: string;
  entries: Record<string, AttendanceStatus>;
  reasons?: Record<string, string>;
  recordedBy: string;
  recordedByName: string;
  createdAt: string;
  updatedAt?: string;
}

/* ==================================================================
   DISCIPLINE (Step 2)
   ================================================================== */

export type DisciplineCategory =
  | 'lateness'
  | 'uniform'
  | 'bullying'
  | 'fighting'
  | 'academic_misconduct'
  | 'disrespect'
  | 'absenteeism'
  | 'substance_abuse'
  | 'vandalism'
  | 'other';

export type DisciplineSeverity = 'low' | 'medium' | 'high' | 'critical';

export type DisciplineStatus = 'open' | 'resolved' | 'escalated';

export interface DisciplineRecord {
  id: string;
  studentId: string;
  studentName: string;
  classId: string;
  className: string;
  category: DisciplineCategory;
  severity: DisciplineSeverity;
  description: string;
  actionTaken?: string;
  incidentDate: string;
  status: DisciplineStatus;
  parentNotified?: boolean;
  notes?: string;
  recordedBy: string;
  recordedByName: string;
  createdAt: string;
  updatedAt?: string;
  updatedBy?: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

/* ==================================================================
   ACHIEVEMENTS — the positive counterpart to Discipline. Commendations,
   awards and milestones per student, surfaced to parents.
   ================================================================== */

export type AchievementCategory =
  | 'academic'
  | 'sport'
  | 'arts'
  | 'leadership'
  | 'citizenship'
  | 'improvement'
  | 'service'
  | 'attendance'
  | 'other';

/** Scope of the achievement — a positive analogue of discipline severity. */
export type AchievementLevel =
  | 'class'
  | 'school'
  | 'zonal'
  | 'regional'
  | 'national'
  | 'international';

export interface AchievementRecord {
  id: string;
  studentId: string;
  studentName: string;
  classId: string;
  className: string;
  category: AchievementCategory;
  level: AchievementLevel;
  /** Short headline, e.g. "1st place — Regional Maths Olympiad". */
  title: string;
  description?: string;
  awardDate: string;
  /** Optional merit points. */
  points?: number;
  parentNotified?: boolean;
  recordedBy: string;
  recordedByName: string;
  createdAt: string;
  updatedAt?: string;
  updatedBy?: string;
}

/* ==================================================================
   PARENT ACCOUNTS (Phase 2)
   ================================================================== */

/** For student role: which pupil record a login belongs to. From Al-Nur. */
export interface StudentAccountLink {
  /** Student.id this login belongs to. */
  studentId: string;
  /** Denormalised for display before the roster subscription resolves. */
  studentName?: string;
  /** Class at the time the account was issued. Display only, and may go
   *  stale: a student account reads NOTHING from the roster, so the name is
   *  denormalised here rather than looked up. */
  classId?: string;
  className?: string;
}

export interface ParentAccountLink {
  /** Guardian.id from the student's admissions.guardians array */
  guardianId: string;
  /** Student IDs this parent account can see */
  studentIds: string[];
}

export interface ParentInvitation {
  id: string;
  guardianId: string;
  guardianName: string;
  email: string;
  studentIds: string[];
  studentNames: string[];
  status: 'pending' | 'accepted' | 'expired';
  invitedBy: string;
  invitedByName: string;
  createdAt: string;
  acceptedAt?: string;
  parentUid?: string;
}

/* ==================================================================
   FAMILIES (school-wide sibling grouping)
   ==================================================================

   A Family groups siblings under one record. It is the entity that
   downstream modules GROUP BY — so a parent with three children in
   the school is one comms recipient, one combined statement, one set
   of sibling-discount calculations — without sending or billing three
   times.

   Design (deliberately additive, zero academic-side risk):
     - Guardians are NOT relocated. They remain canonical on
       `student.admissions.guardians` (+ legacy `student.parentContact`).
       The Family carries a single `primaryContact` (the payer / main
       contact) which everything groups around, optionally linked back
       to a real Guardian via `guardianRef`.
     - `Student.familyId` is the authoritative per-student link;
       `Family.studentIds` is a denormalised mirror kept consistent on
       write (fast grouping without a collection scan).
     - Stored in `reportFamilies` (academic `report*` convention, NOT a
       finance collection — families are school-wide).
   ================================================================== */

/**
 * The single send / billing target for a family. Dedups multi-child
 * homes for announcements, finance statements, and fee reminders.
 */
export interface FamilyPrimaryContact {
  /** Display name of the main contact / payer, e.g. "Mr T. Selelo". */
  name: string;
  email?: string;
  /** E.164-ish WhatsApp number, e.g. +2677XXXXXXX */
  whatsapp?: string;
  /** Consent for electronic delivery (statements, reminders, notices). */
  consentToSend?: boolean;
  /**
   * Optional link back to the canonical Guardian this contact was drawn
   * from — a member student's id plus that student's
   * `admissions.guardians[].id`. Keeps the family contact reconcilable
   * with admissions data without relocating guardian records.
   */
  guardianRef?: { studentId: string; guardianId: string };
}

/** Finance billing preference for a family (P1). */
export interface FamilyBillingPlan {
  /** standard = use the school default split; custom = use `splits`;
   *  prepayYear = pay the whole year in advance (single up-front instalment
   *  + prepayment discount). */
  kind: 'standard' | 'custom' | 'prepayYear';
  /** Percentage split for kind==='custom' (sums to ~100), e.g. [80,20]. */
  splits?: number[];
  /** Explicit instalment schedule (preferred over `splits`): each part is a
   *  percentage plus a day-offset from the term's first-instalment anchor
   *  date, so it re-applies every term. e.g.
   *  [{percent:50,offsetDays:0},{percent:50,offsetDays:30}] = half on the
   *  first day of term, half a month later. */
  instalments?: BillingInstalmentRow[];
}

/** One row of an explicit instalment schedule. */
export interface BillingInstalmentRow {
  percent: number;
  /** Days after the run's first-instalment (term-start) anchor date. */
  offsetDays: number;
}

export interface Family {
  /** Firestore doc id, e.g. `fam_1716543210_a1b2c3`. */
  id: string;
  /** Display name, e.g. "Selelo Family". */
  name: string;
  /**
   * Member student ids. Denormalised mirror of the students whose
   * `familyId` points here; kept consistent on every membership write.
   */
  studentIds: string[];
  /** Single comms/billing contact — the dedup target. */
  primaryContact: FamilyPrimaryContact;
  /** Billing / correspondence address (used by finance statements). */
  billingAddress?: string;
  notes?: string;
  /** How this family's term invoices are split / prepaid (P1). */
  billingPlan?: FamilyBillingPlan;
  /** Mirrors students so the demo reseed can wipe + rewrite cleanly. */
  isDemo?: boolean;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * School-level settings doc. Stored as `reportSchoolSettings/default`.
 * Admin-configurable toggles that affect the parent portal, etc.
 */
export interface SchoolSettings {
  id: string;
  /** Whether parents can see the Discipline tab on their child's profile */
  parentDisciplineVisible: boolean;
  /** Send parents a same-day alert when their child is marked absent.
   *  Undefined = enabled (opt-out). */
  parentAbsenceAlerts?: boolean;
  /** Also email the absence alert (in addition to the in-app + push
   *  notification). Undefined = enabled. */
  parentAbsenceEmail?: boolean;
  /* ---- Parent-portal maintenance controls (DISPLAY-ONLY) ----
   * These NEVER affect the fee-arrears academic hold (financeAcademicHolds /
   * familyHasArrears). They only change what parents see. */
  /** A dismissible-style banner shown across parent surfaces. */
  parentNotice?: { enabled: boolean; message: string };
  /** Hide fee balances/statement from parents (e.g. while balances are being
   *  reconciled). Undefined/true = visible. Does NOT change arrears gating. */
  parentFeesVisible?: boolean;
  /** Full parent-portal maintenance — parents see only a maintenance screen. */
  parentPortalMaintenance?: { enabled: boolean; message: string };
  updatedAt?: string;
  updatedBy?: string;
}

/* ------------------------------------------------------------------
 * Term calendar — admin-editable term dates + reopen dates, the single
 * source of truth that feeds report cards (and anywhere term dates are
 * shown). Falls back to lib/school.config when a term is unset. Stored at
 * reportSchoolSettings/termCalendar. See lib/termDates.ts.
 * ---------------------------------------------------------------- */
export interface TermCalendarEntry {
  /** ISO date (YYYY-MM-DD). */
  start: string;
  /** ISO date (YYYY-MM-DD). */
  end: string;
  /** ISO date pupils RETURN after this term (= the next term's start). */
  reopens: string;
}
export interface TermCalendarDocument {
  id: string; // 'termCalendar'
  year: string;
  terms: Record<string, TermCalendarEntry>;
  updatedAt?: string;
  updatedBy?: string;
}

/* ==================================================================
   PROMOTION / ENROLLMENT (Step 5)
   ================================================================== */

export type EnrollmentStatus =
  | 'enrolled'
  | 'promoted'
  | 'repeated'
  | 'transferred'
  | 'withdrawn'
  | 'graduated';

export interface EnrollmentRecord {
  id: string;
  studentId: string;
  studentName: string;
  academicYear: string;
  classId: string;
  className: string;
  formLevel: string;
  status: EnrollmentStatus;
  promotedToClassId?: string;
  promotedToClassName?: string;
  notes?: string;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
}

export interface PromotionRuleSet {
  id: string;
  rules: Record<string, string>;
  graduatingClassIds: string[];
  updatedAt?: string;
  updatedBy?: string;
}

/* ==================================================================
   ANNOUNCEMENTS (Phase 3 Step 5)
   ================================================================== */

export type AnnouncementTarget =
  | 'all'
  | 'staff'
  | 'parents'
  | 'admin'
  | 'teachers'
  | 'classTeachers'
  | 'hod';

export type AnnouncementPriority = 'normal' | 'important' | 'urgent';

export interface Announcement {
  id: string;
  title: string;
  body: string;
  /** Who should see this */
  target: AnnouncementTarget;
  /** Specific class IDs if targeting a class (empty = all) */
  targetClassIds?: string[];
  priority: AnnouncementPriority;
  /** ISO date after which the announcement auto-hides */
  expiresAt?: string;
  /** User IDs who have dismissed this */
  dismissedBy?: string[];
  /** Is this pinned to top */
  pinned?: boolean;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt?: string;
}

/* ==================================================================
   NOTIFICATIONS / AUDIT (parity with v1)
   ================================================================== */




export interface AppNotification {
  id: string;
  recipientId: string;
  type: string;
  title: string;
  message: string;
  details?: Record<string, unknown>;
  read: boolean;
  createdAt: string;
}

export interface AuditEvent {
  id: string;
  action: string;
  category: 'auth' | 'data' | 'reports' | 'admin' | 'flags' | 'comments';
  actorId?: string;
  actorEmail?: string;
  targetId?: string;
  targetType?: string;
  details?: Record<string, unknown>;
  createdAt: string;
}

/* ==================================================================
   GRADING CONFIG
   ================================================================== */

export interface GradeBoundary {
  grade: string;
  min: number;
  max: number;
  /** RGB for jsPDF. */
  color: [number, number, number];
}

export interface AppSettings {
  schoolName: string;
  gradeBoundaries: GradeBoundary[];
  /** Default pass mark; form-level overrides handled in grading.ts. */
  passMark: number;
  maxFailuresAllowed: number;
}

/* ==================================================================
   CURRICULUM (Phase 4)
   ==================================================================

   Per-subject, per-form-level scheme of work. Owned by HODs, who file
   in topics for each of the three terms; parents read this from the
   portal so they can see what their child is covering.

   Firestore layout:
     reportCurriculum/{subjectId}_{formLevel}
       e.g. "mathematics_3" -> the Form 3 maths curriculum

   The doc holds a `terms` map keyed by TermKey ('Term1' | 'Term2' |
   'Term3'). Each term is an ordered list of topics.
   ================================================================== */

export interface CurriculumTopic {
  /** Stable id — generated locally, NOT a Firestore doc id. */
  id: string;
  title: string;
  description: string;
  /** Free text — typically "Week 1-3" or "Weeks 4-6". */
  weeks: string;
  /** Which CA this topic is examined under, e.g. "CA1" / "CA2". */
  caAssessment: string;
}

export interface CurriculumTerms {
  Term1: { topics: CurriculumTopic[] };
  Term2: { topics: CurriculumTopic[] };
  Term3: { topics: CurriculumTopic[] };
}

export interface ReportCurriculumDocument {
  /** Doc id == `${subjectId}_${formLevel}` */
  id: string;
  subjectId: string;
  subjectName: string;
  /** '1'..'5' | 'AS' | 'A2' */
  formLevel: string;
  /** Department the subject belongs to (denormalised from Subject/Teacher). */
  department?: string;
  terms: CurriculumTerms;
  updatedAt: string;
  updatedBy?: string;
  updatedByName?: string;
  createdAt?: string;
}

/* ==================================================================
   APPLICATIONS (public intake → review → admission)
   ==================================================================

   A prospective student (or their parent) submits an application via
   the PUBLIC /apply page. The submission lands in `reportApplications`
   with status='pending'. Admin/headteacher/admissions staff review,
   make decisions (class, subjects), then call admitApplication which
   clones it into reportStudents and deletes the application doc.

   Applications are NEVER written directly from the client — the
   public submitApplication Cloud Function does it server-side (so it
   can rate-limit, validate, send email and assign a reference number
   atomically).
   ================================================================== */

export type ApplicationStatus =
  | 'pending'
  | 'under_review'
  | 'approved'
  | 'rejected'
  /**
   * Written by the onApplicationCreated function when the same applicant
   * submits twice inside 24 hours: the second document is stamped
   * 'duplicate', pointed at the first via `duplicateOf`, and given the
   * original's reference number so the parent sees one application.
   *
   * It was missing from this union for as long as the feature existed, and
   * the first real duplicate — 3 September 2026 — took the whole admissions
   * list down, because the screen looked its status up in a map that had no
   * entry for it.
   */
  | 'duplicate';

export interface ApplicationDecisions {
  assignedClassId?: string;
  /** Future-use: subjectId -> groupId for split-group classes. */
  subjectGroups?: Record<string, string>;
  /** Internal-only notes (not shown to the applicant). */
  internalNotes?: string;
  decidedBy?: string;
  decidedByName?: string;
  decidedAt?: string;
}

export interface ApplicationRecord {
  /**
   * Paperwork the OFFICE attaches — birth certificate, guardian ID, last
   * school report, transfer letter or proof of residence. The application
   * cannot be printed or approved until the four required ones are here.
   * The applicant never uploads: see lib/applicationDocuments.ts.
   */
  documents?: import('@/lib/applicationDocuments').AttachedDocument[];
  /**
   * The entrance test. Recorded by the Academics Office before an
   * application may be approved — the printed form has always had a box for
   * it and nothing ever typed the answer in. See lib/entranceAssessment.ts.
   */
  entranceAssessment?: import('@/lib/entranceAssessment').EntranceAssessment;
  /** Set when the application was approved despite a failed entrance test.
   *  "Approved despite the test" is a different fact from "approved", and
   *  somebody will ask a year later. */
  approvedAgainstAssessment?: boolean;
  /** The Conditions of Admission as they stood when this parent accepted
   *  them, snapshotted so a later edit cannot rewrite what was agreed. */
  acceptedTerms?: boolean;
  acceptedTermsAt?: string;
  acceptedConditions?: string[];
  id: string;
  /** Human-readable reference: APP-2026-0001. */
  referenceNumber: string;
  submittedAt: string;
  submitterIp?: string;
  submitterUserAgent?: string;

  /* ---- mirror of AdmissionsData (the parent fills this in) ---- */
  firstName: string;
  surname: string;
  gender: 'M' | 'F';
  dateOfBirth: string;
  /** Student's place of birth (paper §1). */
  placeOfBirth?: string;
  nationality: string;
  homeLanguage?: string;
  religion?: string;
  /** Student's own ID / passport number (paper §1). */
  idPassportNumber?: string;
  /** Student's own contact details (paper §1). */
  studentEmail?: string;
  studentPhone?: string;
  address: string;
  /** The form they're applying TO, e.g. '1' | '2' | ... | 'AS' | 'A2' */
  applyingForForm: string;
  /** The academic year they're applying for, e.g. '2026'. */
  applyingForYear: string;
  boardingStatus?: 'day' | 'boarding';
  /** Legacy single previous-school string. Kept for backward-compat;
   *  mirrors `previousSchools.last.name` on new submissions. */
  previousSchool?: string;
  /** Structured previous-schooling history (paper §4). */
  previousSchools?: PreviousSchoolsInfo;

  /** Guardians (re-uses the canonical Guardian shape). */
  guardians: Guardian[];

  medical?: MedicalInfo;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  /** Emergency contact email (paper emergency-contact section). */
  emergencyContactEmail?: string;

  /** Siblings already at the school (paper §6). */
  siblings?: SiblingInfo;

  /** Free-text from the applicant. */
  notes?: string;

  /* ---- review workflow ---- */
  status: ApplicationStatus;
  decisions?: ApplicationDecisions;
  rejection?: {
    reason: string;
    rejectedBy: string;
    rejectedByName: string;
    rejectedAt: string;
  };

  /** Tracking — set when the application has been admitted. */
  admittedStudentId?: string;
  admittedAt?: string;
  admittedBy?: string;

  /** Up-front fee receipts confirmed at approval (application +
   *  registration). Both must be received before the applicant can be
   *  admitted; they are not re-billed on the joining invoice. */
  feeReceipts?: {
    application?: AdmissionFeeReceipt;
    registration?: AdmissionFeeReceipt;
  };

  /** Family resolution, set at approval and a prerequisite for admit:
   *  either link to an existing family or quick-create a new one (when
   *  the applicant is the first child of a family). Applied on admit. */
  familyLink?: ApplicationFamilyLink;

  updatedAt: string;
}

export interface AdmissionFeeReceipt {
  received: boolean;
  amount?: number;
  reference?: string;
  receivedAt?: string;
  byName?: string;
}

export interface ApplicationFamilyLink {
  mode: 'existing' | 'new';
  /** Set when mode === 'existing'. */
  familyId?: string;
  /** Display name — the existing family's name, or the new family's name. */
  familyName?: string;
}

/** Editable §7 boilerplate for the printed application form. Lives at
 *  Firestore `reportSchoolSettings/admissionFormText`; edited by admins
 *  and read by the print PDF (falls back to lib/admissionFormDefaults). */
export interface AdmissionFormText {
  id?: string;
  /** Fee statement paragraph. */
  feeStatement: string;
  /** "Documents required" numbered list. */
  documentsRequired: string[];
  /** "Documents received" office checklist. */
  documentsReceived: string[];
  /** The numbered Conditions of Admission. */
  conditions: string[];
  updatedAt?: string;
  updatedBy?: string;
}

/* ==================================================================
   STUDENT DOCUMENTS (Round 3)
   ==================================================================

   Per-student document storage. Documents live in:
     Firebase Storage:  studentDocuments/{studentId}/{docId}/{filename}
     Firestore:         reportStudentDocuments/{docId}

   Flat Firestore collection (not a subcollection) so we can query all
   documents for a student with a single `where('studentId','==',id)`.

   Access (enforced by rules):
     Write (upload / delete)  — admin, headteacher, admissions
     Read (list / download)   — admin, headteacher, admissions, AND
                                the linked parent (for their child)
   ================================================================== */

/**
 * Predefined document types. 'other' uses `customLabel` for the
 * display name.
 */
export type StudentDocumentType =
  | 'birth_certificate'
  | 'passport'
  | 'residence_permit'
  | 'parent_id'
  | 'previous_transcript'
  | 'vaccination_record'
  | 'other';

export interface StudentDocument {
  /** Firestore doc id (e.g. `doc_1716543210_a1b2c3`). */
  id: string;
  /** Which student this document belongs to. */
  studentId: string;
  /** One of the predefined types. */
  type: StudentDocumentType;
  /** Display name when type === 'other'; ignored otherwise. */
  customLabel?: string;
  /** Original filename as uploaded (preserved for the download UX). */
  filename: string;
  /** Sniffed content type, e.g. `application/pdf` or `image/png`. */
  contentType: string;
  /** Byte size of the stored file. */
  size: number;
  /** Storage path; lets us request a fresh download URL. */
  storagePath: string;
  /** Optional free-text annotation by the uploader. */
  notes?: string;
  uploadedAt: string;
  uploadedBy: string;
  uploadedByName?: string;
}

/** Labels for the predefined types. */
export const STUDENT_DOCUMENT_TYPE_LABELS: Record<StudentDocumentType, string> = {
  birth_certificate: 'Birth Certificate',
  passport: 'Passport',
  residence_permit: 'Residence Permit',
  parent_id: "Parent's ID",
  previous_transcript: 'Previous School Transcript',
  vaccination_record: 'Vaccination Record',
  other: 'Other',
};

/* ==================================================================
   ADMISSION-WINDOW SETTINGS (admin-configurable)
   ==================================================================

   Stored as `reportSchoolSettings/admissionWindow`. If the doc is
   missing or `acceptingApplications` is true with an empty
   `allowedForms` array, the public form accepts applications for
   ALL forms (the default). If `allowedForms` is non-empty, only
   those forms appear in the dropdown. If `acceptingApplications`
   is false, the public form shows a "not currently accepting
   applications" message instead.
   ================================================================== */
export interface AdmissionWindowSettings {
  id: string;
  acceptingApplications: boolean;
  /** Empty array == all forms allowed. */
  allowedForms: string[];
  /** Optional message shown on the public form (markdown-lite). */
  message?: string;
  updatedAt?: string;
  updatedBy?: string;
}



/* Study portal types — shared with Al-Nur, see types/study.ts. */
export * from './study';
